import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip,
  CircularProgress,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Divider,
  Card,
  CardHeader,
  CardContent,
  TextField
} from '@mui/material';
import {
  ViewColumn as SplitViewIcon,
  ArrowLeft,
  ArrowRight,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  SaveAlt as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  SmartToy as AutomationIcon,
  PlayCircle as TestRunIcon,
  Schedule as ScheduleIcon,
  Devices as CrossBrowserIcon,
  Api as ApiTestIcon,
  DataObject as DataDrivenIcon,
  Assessment as ReportingIcon,
  Build as FrameworkIcon,
  Settings as ConfigIcon,
  Edit as EditIcon,
  Save as SaveEditIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import ActionButtons from './ActionButtons';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useNotification from '../../hooks/useNotification';
import { EditableTextField } from '../atoms';
import useDocuments from '../../hooks/useDocuments';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import { extractSectionsByTaskType } from '../../utils/markdownParser';

// Import full screen functionality
import useFullScreen from '../../hooks/useFullScreen';
import FullScreenButton from '../common/FullScreenButton';
import PermissionGate from '../common/PermissionGate';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import ImprovementModal from '../atoms/ImprovementModal';

// Import Mermaid for diagram rendering
import mermaid from 'mermaid';

// Initialize mermaid config
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: false, htmlLabels: true },
    sequence: { diagramMarginX: 50, diagramMarginY: 10 },
    classDiagram: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false }
  });
} catch (error) {
  console.error('Error initializing mermaid:', error);
}

/**
 * A split-view layout component that shows conversation on the left
 * and automated testing documentation in a structured format on the right
 */
const SplitViewAutomatedTesting = ({ conversation, children, isStreaming, streamingContent, projectId }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [parsedTestCases, setParsedTestCases] = useState({
    strategy: '',
    uiAutomation: [],
    apiAutomation: [],
    dataDriven: [],
    crossBrowser: [],
    ciCdIntegration: [],
    maintenance: '',
    reporting: ''
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    strategy: false,
    uiAutomation: false,
    apiAutomation: false,
    dataDriven: false,
    crossBrowser: false,
    ciCdIntegration: false,
    maintenance: false,
    reporting: false
  });
  // Use the validation hook instead of local state
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSection,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'automated-testing',
    initialState: {
      strategy: false,
      uiAutomation: false,
      apiAutomation: false,
      dataDriven: false,
      crossBrowser: false,
      ciCdIntegration: false,
      maintenance: false,
      reporting: false
    }
  });
  const { showNotification } = useNotification();
  const { createDocument } = useDocuments();
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Handler for applying improved content
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;
    try {
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        return;
      }
      closeImprovementModal();
      showNotification({ message: 'Section improved successfully', type: 'success' });
    } catch (error) {
      showNotification({ message: 'Failed to apply improvement', type: 'error' });
    }
  };

  const contentRef = useRef('');
  const testingPanelRef = useRef(null);
  
  // Add full screen functionality
  const { 
    isFullScreen, 
    toggleFullScreen, 
    getLeftPanelStyles, 
    getRightPanelStyles,
    getContentStyles 
  } = useFullScreen();
  
  // Mermaid diagram component
  const MermaidDiagram = ({ content, title }) => {
    const mermaidRef = useRef(null);
    const [diagramSvg, setDiagramSvg] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    useEffect(() => {
      if (!content || !mermaidRef.current) return;
      
      const renderDiagram = async () => {
        try {
          setIsLoading(true);
          setError(null);
          
          const uniqueId = `automated-testing-mermaid-${Math.random().toString(36).substr(2, 9)}`;
          
          // Render the mermaid diagram
          const { svg } = await mermaid.render(uniqueId, content);
          setDiagramSvg(svg);
        } catch (err) {
          console.error('Error rendering mermaid diagram:', err);
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      
      renderDiagram();
    }, [content]);
    
    if (isLoading) {
      return (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: 200,
          backgroundColor: '#e8f4f8',
          border: '1px solid #2196f3',
          borderRadius: 1,
          mb: 2
        }}>
          <CircularProgress size={24} sx={{ color: '#2196f3' }} />
          <Typography variant="body2" sx={{ ml: 2, color: '#1976d2' }}>
            Rendering automation diagram...
          </Typography>
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Could not render automation diagram: {error}
          </Typography>
        </Alert>
      );
    }
    
    if (!diagramSvg) return null;
    
    return (
      <Box sx={{ 
        mb: 2, 
        p: 2, 
        backgroundColor: '#e8f4f8', 
        border: '1px solid #2196f3', 
        borderRadius: 1,
        '& svg': { maxWidth: '100%', height: 'auto' }
      }}>
        <Typography variant="caption" sx={{ 
          display: 'block', 
          mb: 1, 
          fontWeight: 500,
          color: '#1565c0'
        }}>
          🤖 {title || 'Automated Testing Flow'}
        </Typography>
        <div 
          ref={mermaidRef}
          dangerouslySetInnerHTML={{ __html: diagramSvg }}
        />
      </Box>
    );
  };
  
  // Determine if we should show the split view
  const isAutomatedTestingConversation = conversation && (conversation?.taskId || conversation?.task_id) === 'automated-testing';
  
  console.log('SplitViewAutomatedTesting rendering', { 
    conversationId: conversation?.id, 
    taskId: conversation?.taskId, 
    isAutomatedTestingConversation, 
    isStreaming 
  });

  // Handler for expanding/collapsing sections
  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Edit handlers
  const handleStartEdit = (section, content) => {
    setEditingSection(section);
    if (Array.isArray(content)) {
      // Convert array to editable string format
      const contentString = content.map(item => 
        `**${item.title}**\n${item.description || ''}\n${item.code ? '```\n' + item.code + '\n```' : ''}`
      ).join('\n\n');
      setEditedContent(contentString);
    } else {
      setEditedContent(content || '');
    }
  };

  const handleSaveEdit = (section) => {
    // Update the content in parsedTestCases
    setParsedTestCases(prev => ({
      ...prev,
      [section]: editedContent
    }));
    
    // Reset edit state
    setEditingSection(null);
    setEditedContent('');
    
    showNotification({
      message: 'Content updated successfully',
      type: 'success'
    });
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setEditedContent('');
  };

  // Process human-readable content
  const processHumanReadableContent = (content) => {
    if (!content) return '';
    
    // Remove markdown syntax for better readability
    let processed = content
      .replace(/^#{1,6}\s+/gm, '') // Remove heading markers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markers
      .replace(/\*(.*?)\*/g, '$1') // Remove italic markers
      .replace(/`([^`]+)`/g, '$1') // Remove inline code markers
      .replace(/^[\s]*[-*+]\s+/gm, '• ') // Convert list markers to bullets
      .replace(/^[\s]*\d+\.\s+/gm, (match, offset, string) => {
        // Convert numbered lists to bullets, preserving the number for reference
        const num = match.match(/\d+/)[0];
        return `${num}. `;
      })
      .trim();
    
    return processed;
  };
  
  // Detect and extract mermaid diagrams
  const extractMermaidDiagrams = (content) => {
    const mermaidRegex = /```mermaid\s*\n([\s\S]*?)\n```/gi;
    const diagrams = [];
    let match;
    
    while ((match = mermaidRegex.exec(content)) !== null) {
      diagrams.push(match[1].trim());
    }
    
    return diagrams;
  };
  
  // Extract code blocks
  const extractCodeBlocks = (content) => {
    const codeBlockRegex = /```(?:(\w+)\s*)?\n([\s\S]*?)\n```/gi;
    const codeBlocks = [];
    let match;
    
    while ((match = codeBlockRegex.exec(content)) !== null) {
      const language = match[1] || 'text';
      const code = match[2].trim();
      
      // Skip mermaid diagrams as they're handled separately
      if (language.toLowerCase() !== 'mermaid') {
        codeBlocks.push({ language, code });
      }
    }
    
    return codeBlocks;
  };
  
  // Enhanced section rendering with comprehensive content processing
  const renderSection = (content, title, sectionKey) => {
    if (!content || (typeof content === 'string' && !content.trim())) {
      return null;
    }
    
    // Extract different types of content
    const mermaidDiagrams = extractMermaidDiagrams(content);
    const codeBlocks = extractCodeBlocks(content);
    const humanReadableContent = processHumanReadableContent(
      content.replace(/```[\s\S]*?```/g, '') // Remove all code blocks for human-readable processing
    );
    
    return (
      <Box sx={{ width: '100%' }}>
        {/* Mermaid Diagrams */}
        {mermaidDiagrams.map((diagram, index) => (
          <MermaidDiagram 
            key={`mermaid-${index}`}
            content={diagram}
            title={`${title} - Diagram ${index + 1}`}
          />
        ))}
        
        {/* Human-readable content */}
        {humanReadableContent && (
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.6,
                fontSize: '14px',
                color: '#2c3e50',
                whiteSpace: 'pre-line'
              }}
            >
              {humanReadableContent}
            </Typography>
          </Box>
        )}
        
        {/* Code blocks with syntax highlighting for automation */}
        {codeBlocks.map((block, index) => (
          <Box key={`code-${index}`} sx={{ mb: 2 }}>
            <Box sx={{
              backgroundColor: '#f0f8ff',
              border: '1px solid #2196f3',
              borderRadius: 1,
              overflow: 'hidden'
            }}>
              <Box sx={{
                backgroundColor: '#e3f2fd',
                px: 2,
                py: 1,
                borderBottom: '1px solid #2196f3'
              }}>
                <Typography variant="caption" sx={{ 
                  fontWeight: 500,
                  color: '#1565c0',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  {block.language === 'javascript' || block.language === 'js' ? '🟨 JavaScript/Automation' :
                   block.language === 'typescript' || block.language === 'ts' ? '🔷 TypeScript/Automation' :
                   block.language === 'python' || block.language === 'py' ? '🐍 Python/Selenium' :
                   block.language === 'java' ? '☕ Java/TestNG' :
                   block.language === 'csharp' || block.language === 'cs' ? '🔵 C#/Selenium' :
                   block.language === 'cucumber' || block.language === 'gherkin' ? '🥒 Cucumber/BDD' :
                   block.language === 'yaml' || block.language === 'yml' ? '📋 CI/CD Pipeline' :
                   block.language === 'bash' || block.language === 'shell' ? '🔧 Shell Script' :
                   block.language === 'dockerfile' ? '🐳 Docker Config' :
                   `🤖 ${block.language.toUpperCase()}`}
                </Typography>
              </Box>
              <Box sx={{
                p: 2,
                backgroundColor: '#fafcff',
                fontFamily: 'monospace',
                fontSize: '13px',
                lineHeight: 1.4,
                overflow: 'auto',
                '& pre': { margin: 0, whiteSpace: 'pre-wrap' }
              }}>
                <pre>{block.code}</pre>
              </Box>
            </Box>
          </Box>
        ))}
        
        {/* Fallback: show full content if no specific content was extracted */}
        {!humanReadableContent && mermaidDiagrams.length === 0 && codeBlocks.length === 0 && (
          <Box sx={{ 
            p: 3, 
            backgroundColor: '#e8f4f8', 
            borderRadius: 1,
            border: '1px solid #2196f3',
            mb: 2
          }}>
            <Typography variant="body2" sx={{ 
              color: '#1565c0',
              lineHeight: 1.6,
              whiteSpace: 'pre-line'
            }}>
              {typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Extract test automation content from the latest assistant message or streaming content
  useEffect(() => {
    if (!isAutomatedTestingConversation) return;

    // Get all assistant messages to provide comprehensive context
    const assistantMessages = conversation?.messages
      ?.filter(msg => msg.role === 'assistant') || [];

    // Combine all assistant messages to get a more comprehensive view
    const combinedMessages = assistantMessages.length > 0
      ? assistantMessages.map(msg => msg.content).join('\n\n')
      : '';

    // Use the streaming content in addition to the existing messages
    const content = isStreaming && streamingContent
      ? combinedMessages + '\n\n' + streamingContent
      : combinedMessages;

    // Store the content for export and other operations
    contentRef.current = content;

    // Parse the automation content
    if (content.trim()) {
      console.log('SplitViewAutomatedTesting processing content of length:', content.length);
      
      const automationData = {
        strategy: '',
        uiAutomation: [],
        apiAutomation: [],
        dataDriven: [],
        crossBrowser: [],
        ciCdIntegration: [],
        maintenance: '',
        reporting: ''
      };

      // Extract different sections using keyword matching
      const sections = content.split(/#+\s*/);
      
      sections.forEach(section => {
        const lowerSection = section.toLowerCase();
        
        if (lowerSection.includes('strategy') || lowerSection.includes('approach') || lowerSection.includes('overview')) {
          automationData.strategy = section.trim();
        } else if (lowerSection.includes('ui automation') || lowerSection.includes('web automation') || lowerSection.includes('selenium')) {
          automationData.uiAutomation.push({ title: 'UI Automation', description: section.trim(), code: '' });
        } else if (lowerSection.includes('api automation') || lowerSection.includes('api testing') || lowerSection.includes('rest')) {
          automationData.apiAutomation.push({ title: 'API Automation', description: section.trim(), code: '' });
        } else if (lowerSection.includes('data driven') || lowerSection.includes('data-driven') || lowerSection.includes('test data')) {
          automationData.dataDriven.push({ title: 'Data-Driven Testing', description: section.trim(), code: '' });
        } else if (lowerSection.includes('cross browser') || lowerSection.includes('browser testing') || lowerSection.includes('compatibility')) {
          automationData.crossBrowser.push({ title: 'Cross-Browser Testing', description: section.trim(), code: '' });
        } else if (lowerSection.includes('ci/cd') || lowerSection.includes('pipeline') || lowerSection.includes('jenkins') || lowerSection.includes('github actions')) {
          automationData.ciCdIntegration.push({ title: 'CI/CD Integration', description: section.trim(), code: '' });
        } else if (lowerSection.includes('maintenance') || lowerSection.includes('flaky tests') || lowerSection.includes('test stability')) {
          automationData.maintenance = section.trim();
        } else if (lowerSection.includes('reporting') || lowerSection.includes('dashboard') || lowerSection.includes('metrics')) {
          automationData.reporting = section.trim();
        }
      });

      setParsedTestCases(automationData);
    }
  }, [conversation, isStreaming, streamingContent, isAutomatedTestingConversation]);

  // Auto-scroll panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && testingPanelRef.current && isAutomatedTestingConversation) {
      setTimeout(() => {
        if (testingPanelRef.current) {
          testingPanelRef.current.scrollTop = testingPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isAutomatedTestingConversation]);

  // Handler for exporting content
  const handleExport = () => {
    try {
      setIsExporting(true);
      const content = contentRef.current;

      if (!content) {
        showNotification({
          message: "No automated testing content to export",
          type: "warning"
        });
        setIsExporting(false);
        return;
      }

      const date = new Date().toISOString().split('T')[0];
      const filename = `automated-testing-${date}.md`;
      const blob = new Blob([content], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setIsExporting(false);
        showNotification({
          message: "Automated testing document exported successfully",
          type: "success"
        });
      }, 100);
    } catch (error) {
      console.error("Error exporting document:", error);
      showNotification({
        message: "Failed to export automated testing document",
        type: "error"
      });
      setIsExporting(false);
    }
  };

  // Handler for copying content
  const handleCopy = () => {
    try {
      const content = contentRef.current;
      if (!content) {
        showNotification({
          message: "No automated testing content to copy",
          type: "warning"
        });
        return;
      }

      navigator.clipboard.writeText(content).then(() => {
        showNotification({
          message: "Automated testing document copied to clipboard",
          type: "success"
        });
      });
    } catch (error) {
      console.error("Error copying document:", error);
      showNotification({
        message: "Failed to copy automated testing document to clipboard",
        type: "error"
      });
    }
  };

  // Check if we have any content to display
  const hasContent = Object.values(parsedTestCases).some(section => {
    if (typeof section === 'string') {
      return section.trim().length > 0;
    } else if (Array.isArray(section)) {
      return section.length > 0;
    }
    return false;
  });
  
  // Only show split view for automated testing conversations
  if (!isAutomatedTestingConversation) {
    return <>{children}</>;
  }

  // Render test cases for a specific category
  const renderTestCases = (testCases, title, icon, section, color) => {
    const hasTestCases = testCases && (
      (Array.isArray(testCases) && testCases.length > 0) ||
      (!Array.isArray(testCases) && testCases.trim() !== '')
    );

    // Always render accordion, even if empty
    return (
      <Accordion
        expanded={expandedSections[section]}
        onChange={() => handleSectionToggle(section)}
        sx={{
          mb: 1.5,
          '&.Mui-expanded': {
            margin: 0,
          }
        }}
      >
        <AccordionSummary
          sx={{
            backgroundColor: '#f5f8fb',
            borderRadius: '4px',
            position: 'relative',
            margin: 0,
            minHeight: 0,
            padding: '10px',
            '&.Mui-expanded': {
              margin: 0,
              minHeight: 0,
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              display: 'none',
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: 0,
            }
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px", maxWidth: '85%', lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                flexDirection: "column",
                justifyContent: "center"
              }}
            >
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleValidateSection(section);
                }}
                sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
              >
                {validatedSections[section] ? (
                  <ValidatedIcon size={25} memberType={getValidatorType(section)} />
                ) : (
                  <UnvalidatedIcon size={25} />
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  transform: expandedSections[section] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                }}
              >
                <ExpandMoreIcon fontSize="small" />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails 
          sx={{ 
            p: 2, 
            backgroundColor: '#ffffff', 
            position: 'relative',
            cursor: editingSection === section ? 'default' : 'pointer'
          }}
          onClick={() => {
            if (editingSection !== section && hasTestCases) {
              handleStartEdit(section, testCases);
            }
          }}
        >
          <Box sx={{ width: '93%', textAlign: 'justify', minHeight: '200px' }}>
            {editingSection === section ? (
              // Edit mode
              <TextField
                fullWidth
                multiline
                minRows={6}
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '14px',
                    fontFamily: 'monospace',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderWidth: '1px',
                    },
                  },
                }}
              />
            ) : (
              // Display mode
              hasTestCases ? (
                <Box sx={{ mb: 3 }}>
                  {/* Enhanced section content with comprehensive rendering */}
                  {!Array.isArray(testCases) && testCases && renderSection(testCases, title, section)}

                  {/* Section content for array of test cases */}
                  {Array.isArray(testCases) && testCases.map((testCase, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2, overflow: 'visible', boxShadow:"none !important", border: 0 }}>
                      <CardHeader
                        title={
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: "16px" }}>
                            {testCase.title}
                          </Typography>
                        }
                        sx={{
                          py: 1.5,
                          backgroundColor: '#f5f8fb',
                          '& .MuiCardHeader-content': { overflow: 'hidden' }
                        }}
                      />
                      <CardContent sx={{ pt: 1.5, pb: 1 }}>
                        {testCase.description && renderSection(testCase.description, testCase.title, `${section}_${index}`)}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No {title.toLowerCase()} available yet. Ask the AI to provide automated test strategies for this category.
                </Typography>
              )
            )}
          </Box>
          <Box sx={{
            position: 'absolute',
            right: 5,
            top: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            zIndex: 2
          }}>
            {/* Show edit/save/cancel buttons OR action buttons based on edit mode */}
            {editingSection === section ? (
              // Edit mode - show only save and cancel
              <>
                <Box sx={{ 
                  mb: 1,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                }}>
                  <Tooltip title="Save" placement="left">
                    <IconButton
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSaveEdit(section);
                      }}
                    >
                      <SaveEditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ 
                  mb: 1,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '4px',
                }}>
                  <Tooltip title="Cancel" placement="left">
                    <IconButton
                      size="small"
                      sx={{ p: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCancelEdit();
                      }}
                    >
                      <CancelIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            ) : (
              // Normal mode - show edit button and action buttons
              <>
                {hasTestCases && (
                  <Box sx={{ 
                    mb: 1,
                    width: 32,
                    height: 32,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                  }}>
                    <Tooltip title="Edit" placement="left">
                      <IconButton
                        size="small"
                        sx={{ p: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEdit(section, testCases);
                        }}
                      >
                        <EditIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <ActionButtons
                  item={{ name: title, description: `${title} for this project` }}
                  categoryIndex={section}
                  itemIndex={0}
                  onImprove={() => handleImproveClick({ id: title, title, content: '' })}
                  onExplain={() => showNotification({
                    message: `Requesting explanation for ${title.toLowerCase()}`,
                    type: "info"
                  })}
                  onCopy={() => {
                    try {
                      let contentToCopy = '';
                      if (Array.isArray(testCases)) {
                        contentToCopy = testCases.map(tc =>
                          `${tc.title}\n${tc.description || ''}\n${tc.code ? '```\n' + tc.code + '\n```' : ''}`
                        ).join('\n\n');
                      } else {
                        contentToCopy = testCases || '';
                      }

                      navigator.clipboard.writeText(contentToCopy);
                      showNotification({
                        message: `${title} copied to clipboard`,
                        type: "success"
                      });
                    } catch (error) {
                      showNotification({
                        message: `Failed to copy ${title.toLowerCase()} to clipboard`,
                        type: "error"
                      });
                    }
                  }}
                  onDelete={() => showNotification({
                    message: `This action would delete ${title.toLowerCase()} information`,
                    type: "warning"
                  })}
                  vertical={true}
                          isApproved={!!validatedSections[section]}
                />
              </>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      mt: 5
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(sidebarCollapsed),
          overflow: 'auto',
          transition: 'flex 0.3s ease',
          height: '100%',
          p: 2,
        }}>
          {children}
        </Box>

        {/* Automated testing panel */}
        <Box
          ref={testingPanelRef}
          sx={{
            ...getRightPanelStyles(sidebarCollapsed),
            overflow: 'auto',
            display: 'block',
            transition: 'flex 0.3s ease',
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: 2,
              backgroundColor: 'white',
              height: '97%',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#485869' }}>
                  Automated Testing
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Export button */}
                <PermissionGate permission="conversation.content.download">
                  <Tooltip title="Export as markdown">
                    <IconButton
                      size="small"
                      onClick={handleExport}
                      disabled={isExporting || !contentRef.current}
                      sx={{ color: '#485869' }}
                    >
                      {isExporting ?
                        <CircularProgress size={18} /> :
                        <DownloadIcon fontSize="small" />
                      }
                    </IconButton>
                  </Tooltip>
                </PermissionGate>

                {/* Copy button */}
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    disabled={!contentRef.current}
                    sx={{ color: '#485869' }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                {/* Full screen toggle */}
                <FullScreenButton
                  isFullScreen={isFullScreen}
                  onToggle={toggleFullScreen}
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'automated-testing'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={0}
                currentUserMemberType={currentUserMemberType}
            />
            {/* Scrollable content section */}
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Show automated testing content or placeholder */}
              {isStreaming && !hasContent ? (
                <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: 0, border: 0 }}>
                  <Typography color="text.secondary">
                    Generating automated testing strategy...
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                </Box>
              ) : (
                <Box>
                  {/* Testing Strategy Section */}
                  {renderTestCases(
                    parsedTestCases.strategy,
                    "Testing Strategy",
                    <AutomationIcon color="primary" />,
                    "strategy",
                    "#1976d2"
                  )}

                  {/* UI Automation Section */}
                  {renderTestCases(
                    parsedTestCases.uiAutomation,
                    "UI Automation",
                    <TestRunIcon sx={{ color: '#4caf50' }} />,
                    "uiAutomation",
                    "#4caf50"
                  )}

                  {/* API Automation Section */}
                  {renderTestCases(
                    parsedTestCases.apiAutomation,
                    "API Automation",
                    <ApiTestIcon sx={{ color: '#ff9800' }} />,
                    "apiAutomation",
                    "#ff9800"
                  )}

                  {/* Data-Driven Testing Section */}
                  {renderTestCases(
                    parsedTestCases.dataDriven,
                    "Data-Driven Testing",
                    <DataDrivenIcon sx={{ color: '#9c27b0' }} />,
                    "dataDriven",
                    "#9c27b0"
                  )}

                  {/* Cross-Browser Testing Section */}
                  {renderTestCases(
                    parsedTestCases.crossBrowser,
                    "Cross-Browser Testing",
                    <CrossBrowserIcon sx={{ color: '#f44336' }} />,
                    "crossBrowser",
                    "#f44336"
                  )}

                  {/* CI/CD Integration Section */}
                  {renderTestCases(
                    parsedTestCases.ciCdIntegration,
                    "CI/CD Integration",
                    <ScheduleIcon sx={{ color: '#2196f3' }} />,
                    "ciCdIntegration",
                    "#2196f3"
                  )}

                  {/* Test Maintenance Section */}
                  {renderTestCases(
                    parsedTestCases.maintenance,
                    "Test Maintenance",
                    <FrameworkIcon sx={{ color: '#795548' }} />,
                    "maintenance",
                    "#795548"
                  )}

                  {/* Reporting & Analytics Section */}
                  {renderTestCases(
                    parsedTestCases.reporting,
                    "Reporting & Analytics",
                    <ReportingIcon sx={{ color: '#607d8b' }} />,
                    "reporting",
                    "#607d8b"
                  )}

                  {/* No automation content found placeholder */}
                  {!hasContent && !isStreaming && (
                      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: 0 }}>
                        <Typography color="text.secondary">
                          No automated testing content found in this conversation yet.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Ask the AI to create comprehensive automated testing strategies and implementation plans.
                        </Typography>
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        onUpdate={handleUpdateImprovedContent}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    </Box>
  );
};

export default SplitViewAutomatedTesting;